import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, FileText, Receipt, CreditCard, ShoppingCart, Megaphone, AlertCircle, MessageSquare, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useApp } from '@/components/AppLayout';
import { notificationApi, type Notification } from '@/lib/api';
import { cn } from '@/lib/utils';

function getNotificationIcon(notification: Notification) {
  switch (notification.notification_document) {
    case 'invoice':
      return <FileText className="h-5 w-5" />;
    case 'quote':
      return <Receipt className="h-5 w-5" />;
    case 'credit_memo':
      return <CreditCard className="h-5 w-5" />;
    case 'purchase_order':
      return <ShoppingCart className="h-5 w-5" />;
  }

  switch (notification.notification_type) {
    case 'action_required':
      return <AlertCircle className="h-5 w-5" />;
    case 'promotion':
      return <Megaphone className="h-5 w-5" />;
    default:
      return <MessageSquare className="h-5 w-5" />;
  }
}

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { currentBusiness } = useApp();
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const fetchUnreadCount = useCallback(async () => {
    if (!currentBusiness?.id) return;
    const response = await notificationApi.getUnreadCount(currentBusiness.id);
    if (response.data) {
      setUnreadCount(response.data.count);
    }
  }, [currentBusiness?.id]);

  const fetchNotifications = useCallback(async () => {
    if (!currentBusiness?.id) return;
    const response = await notificationApi.list(currentBusiness.id, 5);
    if (response.data) {
      setNotifications(response.data);
    }
  }, [currentBusiness?.id]);

  const markAllAsRead = useCallback(async () => {
    if (!currentBusiness?.id || unreadCount === 0) return;
    await notificationApi.markAllRead(currentBusiness.id);
    setUnreadCount(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, [currentBusiness?.id, unreadCount]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (open) {
      fetchNotifications();
      const timeout = setTimeout(() => {
        markAllAsRead();
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [open, fetchNotifications, markAllAsRead]);

  const handleNotificationClick = (notification: Notification) => {
    setOpen(false);
    if (notification.cta_link) {
      navigate(notification.cta_link);
    }
  };

  const handleViewAll = () => {
    setOpen(false);
    navigate(`/${currentBusiness?.id}/notifications`);
  };

  const visibleNotifications = notifications.filter(n => n.show);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-10 w-10 rounded-full hover:bg-muted"
        >
          <Bell className="h-5 w-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[360px] p-0 shadow-lg"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-auto py-1 px-2 text-xs text-primary hover:text-primary"
              onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
            >
              Mark all as read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        <div className="max-h-[400px] overflow-y-auto">
          {visibleNotifications.length === 0 ? (
            <div className="py-12 text-center">
              <div className="h-12 w-12 rounded-full bg-muted mx-auto mb-3 flex items-center justify-center">
                <Bell className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium text-muted-foreground">No notifications</p>
              <p className="text-xs text-muted-foreground/70 mt-1">You're all caught up!</p>
            </div>
          ) : (
            visibleNotifications.map((notification) => (
              <button
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                className={cn(
                  "w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50",
                  !notification.read && "bg-primary/5"
                )}
              >
                {/* Icon */}
                <div className="relative shrink-0 mt-0.5">
                  <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
                    {getNotificationIcon(notification)}
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    "text-sm leading-snug",
                    !notification.read ? "text-foreground" : "text-muted-foreground"
                  )}>
                    {notification.info_text}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className={cn(
                      "text-xs",
                      !notification.read ? "text-primary" : "text-muted-foreground"
                    )}>
                      {formatTimeAgo(notification.created_at)}
                    </span>
                    {notification.cta_title && (
                      <>
                        <span className="text-muted-foreground/50">·</span>
                        <span className="inline-flex items-center text-xs font-medium text-primary">
                          {notification.cta_title}
                          <ChevronRight className="ml-0.5 h-3 w-3" />
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Unread indicator */}
                {!notification.read && (
                  <div className="shrink-0 mt-2">
                    <div className="h-2.5 w-2.5 rounded-full bg-primary" />
                  </div>
                )}
              </button>
            ))
          )}
        </div>

        {/* Footer */}
        {visibleNotifications.length > 0 && (
          <div className="border-t">
            <button
              onClick={handleViewAll}
              className="w-full py-3 text-sm font-medium text-primary hover:bg-muted/50 transition-colors"
            >
              See all notifications
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
